import React, { useState } from 'react';
import {
  useMediaQuery,
  Paper,
  Typography,
  Menu,
  Fade,
  MenuItem,
  Box,
  Dialog,
  DialogTitle,
  InputLabel,
  FormControl,
  Select,
  DialogActions,
  CircularProgress,
  DialogContent,
  DialogContentText,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Button, ACIcon } from '@advisorycloud/react-ui-components';
import {
  deleteAdvisorCCPA,
  deleteCompanyCCPA,
} from '../../services/admin_services';
import { AccountType } from '../../services/utils/types';
import AdminUserCardHeader from './AdminUserHeader';
import {
  updateAdvisorOnboardingStatus,
  updateAdvisorPrimaryMotivation,
  getAdvisorSuggestedOpportunities,
} from '../../services/advisor_services/index';
import { updateUserSegment } from '../../services/company_services';
import { updateCompanyOnboardingStatusCall } from '../../services/company_services';

const useStyles = () => ({
  card: {
    backgroundColor: 'white',
    width: '100%',
    borderRadius: '4px',
    display: 'flex',
    flexDirection: 'column',
    height: 'fit-content',
    justifyContent: 'flex-end',
    alignItems: 'flex-start',
    borderLeft: '5px solid rgb(0, 104, 173)',
    position: 'relative',
  },
  borderContainer: {
    border: '1px solid rgb(230,237,254)',
    padding: '7px',
    position: 'relative',
    width: '99%',
    height: '100%',
  },

  dialogContainer: {
    paddingTop: '10px',
    paddingBottom: '10px',
    paddingLeft: '20px',
    paddingRight: '20px',
  },
  dialogActions: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-evenly',
    flexDirection: 'row',
  },
  modalLoadingContainer: {
    width: '80px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'end',
  },
  separator: {
    width: '100%',
    marginTop: '0px',
  },
  separatorLine: {
    width: '100%',
    borderBottom: '1px solid rgb(230,237,254)',
    bottom: '70px',
    left: '0px',
    position: 'absolute',
  },
  postCardBottom: {
    paddingTop: '30px',
    paddingBottom: '15px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  badge: {
    position: 'absolute',
    top: '0px',
    left: '-2px',
    zIndex: 99,
  },
  badgeText: {
    paddingLeft: '5px',
    paddingRight: '5px',
    borderRadius: '0% 0% 10% 0% / 0% 0% 18% 0%',
    fontSize: '0.6rem',
    color: 'white',
    backgroundColor: 'rgb(0, 104, 173)',
    textTransform: 'uppercase',
  },
  leftButtonsContainer: {
    width: 'fit-content',
    display: 'flex',
  },
  rightButtonsContainer: {
    width: 'fit-content',
    display: 'flex',
    alignItems: 'center',
    alignContent: 'center',
    justifyContent: 'center',
    marginBottom: '-10px',
  },
});

const AdminUserCard = (props) => {
  const {
    // Header props
    status,
    firstName,
    lastName,
    userTitle,
    companyName,
    phoneNumber,
    email,
    type,
    id,
    isSystemAdmin,
    accountLevel,
    lastLoginDate,
    registrationDate,
    updateStatus,
    primaryMotivation,
    // temporal value
    onboardingStatus,
    aobfStatus,
    // FooterProps
    cardLeftButtons,
    cardRightButtons,
    // format
    headerFormat,
    isModalVisible,
    setIsModalVisible,
    avatarImage,
    boardOwnerMail,
    boardType,
    seatsCount,
    boardTotalSeats,
    planCode,
    advisorNumberOfBoards,
    freeTrialEndsOn,
    editUserDisabled,
    editUser,
    editCompanyDisabled,
    editCompany,
    isSuperAdmin,
    userSegments,
  } = props;
  const { t } = useTranslation();
  const isSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const classes = useStyles(isSM);
  const [anchorEl, setAnchorEl] = useState(null);
  const [userSegment, setUserSegment] = useState(userSegments || '');
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [ModalText, setModalText] = useState('');
  const [suggestedBoards, setSuggestedBoards] = useState([]);
  const [suggestedBoardsLoading, setSuggestedBoardsLoading] = useState(false);
  const [suggestedBoardsError, setSuggestedBoardsError] = useState('');
  const [hasRequestedBoards, setHasRequestedBoards] = useState(false);
  const [isBoardsModalOpen, setIsBoardsModalOpen] = useState(false);
  // These are not valid anymore, so any will be considerede as incomplete
  const stepValues = ['step1', 'step2', 'step3', 'step4', 'step5', 'step6'];
  const [selectedOnboardingStatus, setSelectedOnboardingStatus] = useState(
    aobfStatus
      ? aobfStatus
      : stepValues.includes(onboardingStatus)
      ? 'inProcess'
      : onboardingStatus
  );
  const [selectedPrimaryMotivation, setSelectedPrimaryMotivation] = useState(
    primaryMotivation || ''
  );
  const openMenu = Boolean(anchorEl);
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleChange = async (event) => {
    setSelectedOnboardingStatus(event.target.value);
    if (event.target.value === '') return;
    const onboardingProcess =
      event.target.value === 'Complete' ? 'Complete' : 'inProcess';
    if (type === AccountType.ADVISOR) {
      await updateAdvisorOnboardingStatus({
        ADVISOR_ID: id,
        ONBOARDING_STATUS: onboardingProcess,
        ONBOARDING_PROCESS: onboardingProcess,
      });
    } else if (type === AccountType.COMPANY) {
      await updateCompanyOnboardingStatusCall({
        COMPANY_ID: id,
        ONBOARDING_STATUS: onboardingProcess,
        IS_AOBF: true,
      });
    }
  };

  const loadSuggestedBoards = async () => {
    if (type !== AccountType.ADVISOR || !id) return;

    setHasRequestedBoards(true);
    setSuggestedBoardsLoading(true);
    setSuggestedBoardsError('');
    setSuggestedBoards([]);
    setIsBoardsModalOpen(false);

    try {
      const response = await getAdvisorSuggestedOpportunities({
        ADVISOR_ID: id,
      });

      const recommendedBoards =
        response?.data?.suggestOpportunities?.recommendedBoards;

      const boardsList = [
        {
          board: recommendedBoards?.recommendedBoard1,
          company: recommendedBoards?.recommendedBoard1Company,
        },
        {
          board: recommendedBoards?.recommendedBoard2,
          company: recommendedBoards?.recommendedBoard2Company,
        },
        {
          board: recommendedBoards?.recommendedBoard3,
          company: recommendedBoards?.recommendedBoard3Company,
        },
      ].filter((entry) => entry.board || entry.company);

      setSuggestedBoards(boardsList);
    } catch (error) {
      console.error('Error fetching advisor suggested opportunities:', error);
      setSuggestedBoards([]);
      setSuggestedBoardsError('Unable to load board recommendations');
    } finally {
      setSuggestedBoardsLoading(false);
      setIsBoardsModalOpen(true);
    }
  };

  const handleChangeUserSegment = async (event) => {
    setUserSegment(event.target.value);
    await updateUserSegment({
      COMPANY_ID: id,
      USER_SEGMENT: event.target.value,
    });
  };

  const handleChangePrimaryMotivation = async (event) => {
    setSelectedPrimaryMotivation(event.target.value);
    // if (event.target.value === '') return;
    if (type === AccountType.ADVISOR) {
      await updateAdvisorPrimaryMotivation({
        ADVISOR_ID: id,
        PRIMARY_MOTIVATION: event.target.value,
      });
    }
  };

  async function deleteUser() {
    try {
      if (type === AccountType.ADVISOR) {
        await deleteAdvisorCCPA({ USER_ID: id, EMAIL: email });
      } else if (type === AccountType.COMPANY) {
        await deleteCompanyCCPA({ COMPANY_ID: id });
      }
      setModalText('User deleted successfully');
      setIsModalLoading(false);
    } catch (error) {
      console.error('Error deleting user:', error);
      if (
        error.errors &&
        error.errors.length > 0 &&
        error.errors[0].message &&
        JSON.parse(error.errors[0].message) &&
        JSON.parse(error.errors[0].message).error
      ) {
        setModalText(
          'Error deleting user' + JSON.parse(error.errors[0].message).error
        );
      } else {
        setModalText('Error deleting user');
      }
      setIsModalLoading(false);
    }
  }

  function renderRightButtons() {
    if (cardRightButtons === null || cardRightButtons === undefined) {
      return null;
    }

    const filteredButtons = cardRightButtons.filter(
      (element) => element.showButton !== false
    );
    return (
      <>
        <Dialog
          open={isModalVisible}
          sx={classes.dialogContainer}
          maxWidth="sm"
          onClose={() => {
            setIsModalVisible(false);
          }}
        >
          <div style={classes.dialogContainer}>
            <DialogContent
              style={{
                padding: 20,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <DialogContentText>{t('ADVISOR-MODAL-CCPA')}</DialogContentText>
              {ModalText && <DialogContentText>{ModalText}</DialogContentText>}
            </DialogContent>
            <DialogActions sx={classes.dialogActions}>
              {isModalLoading ? (
                <div style={classes.modalLoadingContainer}>
                  <CircularProgress />
                </div>
              ) : (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setIsModalVisible(false);
                    }}
                    style={{
                      paddingTop: isSM ? 30 : 0,
                      paddingBottom: isSM ? 30 : 0,
                      width: 150,
                    }}
                  >
                    {t('CANCEL')}
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      setIsModalLoading(true);
                      deleteUser();
                    }}
                    style={{
                      paddingTop: isSM ? 30 : 0,
                      paddingBottom: isSM ? 30 : 0,
                      width: 150,
                    }}
                  >
                    {t('CONFIRM')}
                  </Button>
                </>
              )}
            </DialogActions>
          </div>
        </Dialog>
        {isSuperAdmin && type === AccountType.COMPANY && (
          <FormControl
            sx={{
              minWidth: '150px',
              marginBottom: '0px',
              marginRight: '10px',
            }}
          >
            <InputLabel id="simple-select-label">User Segments</InputLabel>
            <Select
              labelId="simple-select-label"
              id="simple-select"
              value={userSegment}
              label="User Segments"
              onChange={handleChangeUserSegment}
              sx={{
                height: '45px',
                marginTop: '4px',
                borderRadius: '10px',
              }}
            >
              <MenuItem value="start_your_advisorycloud">
                start_your_advisorycloud
              </MenuItem>
              <MenuItem value="managed_advisory_board">
                managed_advisory_board
              </MenuItem>
              <MenuItem value="insights">insights</MenuItem>
              <MenuItem value="bring_your_own">bring_your_own</MenuItem>
              <MenuItem value="None">None</MenuItem>
            </Select>
          </FormControl>
        )}
        {filteredButtons.map((element, index) => (
          <>
            <div
              key={element.id}
              style={{
                marginLeft: index === 0 ? 0 : '10px',
              }}
            >
              <Button
                color="primary"
                style={{
                  fontSize: '.7rem',
                  whiteSpace: 'nowrap',
                  width: 'auto',
                }}
                onClick={element.menu ? handleClick : element.onClick}
                disabled={element.disabled}
                variant="outlined"
              >
                <ACIcon
                  iconname={element.iconname}
                  style={{
                    fontSize: '1rem',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: 'fit-content',
                    height: 'fit-content',
                    marginBottom: 2,
                  }}
                />
                {element.text}
              </Button>
            </div>
            {element.appendText ? (
              <div
                style={{
                  display: 'inline',
                  color: 'grey',
                  marginLeft: 7,
                  marginRight: 2,
                  paddingTop: 5,
                  marginBottom: 0,
                }}
              >
                {element.appendText}
              </div>
            ) : null}
            {element.menu ? (
              <Menu
                anchorEl={anchorEl}
                open={openMenu}
                onClose={handleClose}
                TransitionComponent={Fade}
                MenuListProps={{
                  'aria-labelledby': 'basic-button',
                }}
                // getContentAnchorEl={null}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                {element.options.map((opp) => (
                  <MenuItem
                    onClick={() => {
                      opp.action();
                      handleClose();
                    }}
                  >
                    {opp.text}
                  </MenuItem>
                ))}
              </Menu>
            ) : null}
          </>
        ))}
      </>
    );
  }

  return (
    <Paper elevation={0} sx={classes.card}>
      <div style={classes.badge}>
        <Typography sx={classes.badgeText}>{accountLevel}</Typography>
      </div>
      <div style={classes.borderContainer}>
        <div style={classes.separator}>
          <AdminUserCardHeader
            status={status}
            updateStatus={updateStatus}
            firstName={firstName}
            lastName={lastName}
            userTitle={userTitle}
            id={id}
            companyName={companyName}
            phoneNumber={phoneNumber}
            email={email}
            accountLevel={accountLevel}
            lastLoginDate={lastLoginDate}
            registrationDate={registrationDate}
            headerFormat={headerFormat}
            avatarImage={avatarImage}
            boardOwnerMail={boardOwnerMail}
            boardType={boardType}
            seatsCount={seatsCount}
            boardTotalSeats={boardTotalSeats}
            planCode={planCode}
            advisorNumberOfBoards={advisorNumberOfBoards}
            freeTrialEndsOn={freeTrialEndsOn}
            editUserDisabled={editUserDisabled}
            editUser={editUser}
            editCompanyDisabled={editCompanyDisabled}
            editCompany={editCompany}
            // temporal value
            onboardingStatus={onboardingStatus}
          />
        </div>

        <div style={classes.separatorLine} />

        <div style={classes.postCardBottom}>
          <div style={classes.leftButtonsContainer}>
            {cardLeftButtons !== null || cardLeftButtons !== undefined ? (
              <>
                {cardLeftButtons.map((element, index) => (
                  <div
                    key={element.id}
                    style={{
                      marginLeft: index === 0 ? 0 : '10px',
                    }}
                  >
                    <Button
                      color="primary"
                      width="95px"
                      style={{ fontSize: '.7rem' }}
                      onClick={element.onClick}
                      disabled={element.disabled}
                    >
                      {element.text}
                    </Button>
                  </div>
                ))}
              </>
            ) : null}
          </div>
          <div style={classes.rightButtonsContainer}>
            {type === AccountType.ADVISOR && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  marginRight: '20px',
                  minWidth: '200px',
                }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={loadSuggestedBoards}
                  disabled={suggestedBoardsLoading}
                  style={{ fontSize: '.7rem', marginBottom: '6px' }}
                >
                  {suggestedBoardsLoading ? (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                      }}
                    >
                      <CircularProgress size={16} style={{ marginRight: 6 }} />
                      Loading...
                    </span>
                  ) : (
                    'Get Board Recommendations'
                  )}
                </Button>
                <Dialog
                  open={isBoardsModalOpen}
                  onClose={() => setIsBoardsModalOpen(false)}
                  fullWidth
                  maxWidth="sm"
                >
                  <DialogTitle sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
                    Board Recommendations
                  </DialogTitle>
                  <DialogContent dividers>
                    {suggestedBoardsLoading ? (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Loading suggestions
                        </Typography>
                      </Box>
                    ) : suggestedBoardsError ? (
                      <Typography variant="body2" color="error">
                        {suggestedBoardsError}
                      </Typography>
                    ) : hasRequestedBoards ? (
                      suggestedBoards.length > 0 ? (
                        suggestedBoards.map((entry, index) => {
                          const { board, company } = entry;

                          const renderArray = (value) =>
                            Array.isArray(value)
                              ? value.filter(Boolean).join(', ')
                              : value || '';

                          return (
                            <Box key={`${board || 'board'}-${index}`} sx={{ mb: 2 }}>
                              {board ? (
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {board}
                                </Typography>
                              ) : null}
                              {company ? (
                                <Box
                                  sx={{
                                    borderLeft: '2px solid #e0e0e0',
                                    pl: 1,
                                    mt: 0.5,
                                  }}
                                >
                                  {company.displayName ? (
                                    <Typography variant="caption" sx={{ display: 'block' }}>
                                      Company: {company.displayName}
                                    </Typography>
                                  ) : null}
                                  {company.headline ? (
                                    <Typography variant="caption" sx={{ display: 'block' }}>
                                      Headline: {company.headline}
                                    </Typography>
                                  ) : null}
                                  {company.storyAndMission ? (
                                    <Typography variant="caption" sx={{ display: 'block' }}>
                                      Mission: {company.storyAndMission}
                                    </Typography>
                                  ) : null}
                                  {company.industryTagIds ? (
                                    <Typography variant="caption" sx={{ display: 'block' }}>
                                      Industries: {renderArray(company.industryTagIds)}
                                    </Typography>
                                  ) : null}
                                  {company.desiredSkillsTagIds ? (
                                    <Typography variant="caption" sx={{ display: 'block' }}>
                                      Desired Skills: {renderArray(company.desiredSkillsTagIds)}
                                    </Typography>
                                  ) : null}
                                  {Array.isArray(company.benefits) &&
                                  company.benefits.length > 0 ? (
                                    <Box sx={{ mt: 0.5 }}>
                                      <Typography
                                        variant="caption"
                                        sx={{ display: 'block', fontWeight: 500 }}
                                      >
                                        Benefits:
                                      </Typography>
                                      {company.benefits.map((benefit, benefitIndex) => (
                                        <Typography
                                          key={`${benefit?.category || 'benefit'}-${benefitIndex}`}
                                          variant="caption"
                                          sx={{ display: 'block', ml: 1 }}
                                        >
                                          {`${benefit?.category || 'General'}: ${
                                            benefit?.details || '—'
                                          }${benefit?.featured ? ' (featured)' : ''}`}
                                        </Typography>
                                      ))}
                                    </Box>
                                  ) : null}
                                </Box>
                              ) : null}
                            </Box>
                          );
                        })
                      ) : (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          No recommendations available
                        </Typography>
                      )
                    ) : (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Request recommendations to see them here.
                      </Typography>
                    )}
                  </DialogContent>
                  <DialogActions>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => setIsBoardsModalOpen(false)}
                    >
                      {t('CLOSE')}
                    </Button>
                  </DialogActions>
                </Dialog>
              </Box>
            )}
            {(isSystemAdmin || isSuperAdmin) && (
              <Box
                sx={{
                  minWidth: '200px',
                  marginRight: '20px',
                }}
              >
                <FormControl fullWidth>
                  <InputLabel id="onboardingStatusLabel">
                    OnboardingStatus
                  </InputLabel>
                  <Select
                    labelId="onboardingStatusLabel"
                    id="onboardingStatus"
                    value={selectedOnboardingStatus}
                    label="OnboardingStatus"
                    sx={{ boderColor: 'blue', height: '35px' }}
                    onChange={handleChange}
                  >
                    <MenuItem value="inProcess">In Process</MenuItem>
                    <MenuItem value="Complete">Complete</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}
            {type === AccountType.ADVISOR && (
              <FormControl fullWidth>
                <InputLabel id="primaryMotivationLabel">
                  Primary Motivation
                </InputLabel>
                <Select
                  labelId="primaryMotivationLabel"
                  id="primaryMotivation"
                  value={selectedPrimaryMotivation}
                  label="Primary Motivation"
                  sx={{
                    boderColor: 'blue',
                    height: '35px',
                    minWidth: '110px',
                    marginRight: '10px',
                  }}
                  onChange={handleChangePrimaryMotivation}
                >
                  <MenuItem value={''}>
                    <em>None</em>
                  </MenuItem>
                  {[
                    'Elite Networking Connections',
                    'Additional Income Potential',
                    'Enhance My Professional Brand',
                    'Reach My Long Term Professional Goals',
                    'Work Less / Flexible Lifestyle',
                  ].map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {renderRightButtons()}
          </div>
        </div>
      </div>
    </Paper>
  );
};

export default AdminUserCard;
